import type { SmtpSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';

const fldList = [
  { name: 'smtpEnabled', required: false },
  { name: 'smtpAddress', required: true },
  { name: 'smtpPort', required: true },
  { name: 'smtpLogin', required: true },
  { name: 'smtpPassword', required: true },
  { name: 'smtpDomain', required: true },
];

export function useEmailSMTP() {
  const { t } = useTranslate('translation');
  const { handleSMTPDetails, state: channelState, onChangeStep } = useChannelContext();

  const SmtpSettingSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const smtpData = data as SmtpSettings;

    if (smtpData.smtpEnabled) {
      if (!smtpData.smtpAddress.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpAddress'],
          message: `${t('SMTP_ADDRESS')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpPort || smtpData.smtpPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPort'],
          message: t('PORT_POSITIVE_NUMBER'),
        });
      }

      if (!smtpData.smtpLogin.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpLogin'],
          message: `${t('SMTP_LOGIN')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpPassword.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPassword'],
          message: `${t('SMTP_PASSWORD')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpDomain.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpDomain'],
          message: `${t('SMTP_DOMAIN')} ${t('IS_REQUIRED')}`,
        });
      }
    }
  });

  const methods = useForm<SmtpSettings>({
    mode: 'all',
    resolver: zodResolver(SmtpSettingSchema),
    defaultValues: channelState.smtpDetails,
  });

  const { reset, handleSubmit, clearErrors, watch } = methods;

  const watchSmtpEnabled = watch('smtpEnabled');

  const onSubmit = handleSubmit((data) => {
    if (!data.smtpEnabled) {
      const defaultValObj = {
        smtpAddress: '',
        smtpPort: 0,
        smtpLogin: '',
        smtpPassword: '',
        smtpDomain: '',
      };

      handleSMTPDetails({ ...defaultValObj, smtpEnabled: false });
      reset({ ...defaultValObj, smtpEnabled: false });
    } else {
      handleSMTPDetails(data);
    }
    onChangeStep('next');
  });

  const btnDisableHandler = () => {
    if (watchSmtpEnabled) {
      const {
        smtpAddress: address,
        smtpPort: port,
        smtpLogin: login,
        smtpPassword: password,
        smtpDomain: domain,
      } = watch();

      return !address || !port || !login || !password || !domain;
    }
    return false;
  };

  useEffect(() => {
    if (!watchSmtpEnabled) clearErrors();
  }, [watchSmtpEnabled, clearErrors]);

  return {
    methods,
    onSubmit,
    watchSmtpEnabled,
    btnDisableHandler,
    t,
  };
}
